import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useMissionContext } from '../../../contexts/MissionContext/hooks/useMissionContext.hook';

import { MissionPage_OneMission_QueryDocument } from '../../Mission/hooks/useFindOneMissionQuery.hook';

import { MissionReportPage_OneReport_QueryDocument } from './useFindOneReportQuery.hook';

export const useQueryInvalidation = (missionReportId: Id) => {
  const queryClient = useQueryClient();

  const { missionId } = useMissionContext();

  const queryKeyToInvalidate = useMemo(
    () => [
      getDocumentName(MissionReportPage_OneReport_QueryDocument),
      { id: missionReportId },
    ],
    [missionReportId],
  );

  const invalidateQueries = useCallback(() => {
    if (!missionReportId) {
      return;
    }

    void queryClient.invalidateQueries({
      queryKey: queryKeyToInvalidate,
    });
  }, [queryClient, queryKeyToInvalidate, missionReportId]);

  const invalidateMissionQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [
        getDocumentName(MissionPage_OneMission_QueryDocument),
        { id: missionId },
      ],
    });
  }, [queryClient, missionId]);

  return {
    invalidateQueries,
    queryKeyToInvalidate,
    invalidateMissionQueries,
  };
};
